import type { ServiceCategory } from '@palihog/shared';
import type { OrderStatus } from '@palihog/shared';
import type { QuotationJobRow, QuoteRow } from '../../lib/types';

export interface ManualQuoteDraft {
  items: string;
  limit: string;
  funding: 'rider_advance' | 'business_advance' | 'not_required';
  distanceKm: string;
  notes: string;
  revisionDescription: string;
  revisionAmount: string;
}

export interface ManualQuoteRequest {
  target_order_id: string;
  distance_meters: number;
  estimated_items: number;
  spending_limit: number;
  purchase_funding: ManualQuoteDraft['funding'];
  quote_notes: string;
  quote_items: Array<{
    description: string;
    quantity: number;
    unitPriceCentavos: number;
    kind: 'price_change';
  }>;
}

export interface ManualQuoteAction {
  label: 'Manual quotation fallback' | 'Create manual revision';
  reason: string;
  revision: boolean;
}

export class ManualQuoteReadbackError extends Error {
  readonly quoteId: string;

  constructor(quoteId: string) {
    super('The quote was written, but its authoritative status could not be confirmed. Do not submit it again; refresh the order status.');
    this.name = 'ManualQuoteReadbackError';
    this.quoteId = quoteId;
  }
}

function parseMoney(value: string, label: string) {
  const normalized = value.trim();
  if (!/^\d+(?:\.\d{1,2})?$/.test(normalized)) throw new Error(`${label} must be a non-negative PHP amount with at most two decimal places.`);
  const [whole, fractional = ''] = normalized.split('.');
  const centavos = Number(whole) * 100 + Number(fractional.padEnd(2, '0'));
  if (!Number.isSafeInteger(centavos)) throw new Error(`${label} is too large.`);
  return centavos;
}

export function createManualQuoteRequest(orderId: string, category: ServiceCategory, draft: ManualQuoteDraft): ManualQuoteRequest {
  const distance = Number(draft.distanceKm);
  if (!Number.isFinite(distance) || distance <= 0 || distance > 2_000_000) {
    throw new Error('Reviewed road distance must be greater than zero and expressed in kilometers.');
  }
  const distanceMeters = Math.ceil(distance * 1_000);
  if (!Number.isSafeInteger(distanceMeters)) throw new Error('Reviewed road distance is invalid.');

  const estimatedItems = parseMoney(draft.items, 'Estimated item cost');
  const spendingLimit = parseMoney(draft.limit, 'Purchase budget');
  if (spendingLimit < estimatedItems) throw new Error('Purchase budget cannot be lower than the estimated item cost.');
  if (category === 'parcel' && (estimatedItems !== 0 || spendingLimit !== 0 || draft.funding !== 'not_required')) {
    throw new Error('Parcel quotations cannot include item charges or purchase funding.');
  }
  if ((category === 'food' || category === 'grocery') && (spendingLimit <= 0 || draft.funding === 'not_required')) {
    throw new Error('Shopping quotations require a positive purchase budget and an authorized funding source.');
  }

  const notes = draft.notes.trim();
  if (notes.length < 10) throw new Error('Enter a specific manual-review reason of at least 10 characters.');

  const description = draft.revisionDescription.trim();
  const hasRevisionAmount = draft.revisionAmount.trim().length > 0;
  if (description && !hasRevisionAmount) throw new Error('Enter the amount for the proposed item change.');
  if (!description && hasRevisionAmount) throw new Error('Describe the proposed item change or clear its amount.');
  const quoteItems = description ? [{
    description,
    quantity: 1,
    unitPriceCentavos: parseMoney(draft.revisionAmount, 'Proposed item change'),
    kind: 'price_change' as const,
  }] : [];

  return {
    target_order_id: orderId,
    distance_meters: distanceMeters,
    estimated_items: estimatedItems,
    spending_limit: spendingLimit,
    purchase_funding: draft.funding,
    quote_notes: notes,
    quote_items: quoteItems,
  };
}

export function manualQuoteAction(
  status: OrderStatus,
  currentQuoteId: string | null,
  job?: QuotationJobRow,
): ManualQuoteAction | null {
  if (currentQuoteId && ['awaiting_customer_approval', 'accepted', 'purchasing_or_collecting'].includes(status)) {
    return {
      label: 'Create manual revision',
      reason: 'An authoritative quote already exists. Any revision creates a new version that the customer must explicitly approve.',
      revision: true,
    };
  }
  if (!['submitted', 'under_review'].includes(status)) return null;
  if (job && ['pending', 'processing', 'retry_wait'].includes(job.status)) return null;
  if (job?.status === 'needs_review') {
    return {
      label: 'Manual quotation fallback',
      reason: job.last_error_message?.trim() || 'Automatic routing or pricing exhausted its bounded attempts and requires reviewed fallback input.',
      revision: false,
    };
  }
  if (!job && !currentQuoteId) {
    return {
      label: 'Manual quotation fallback',
      reason: 'No automatic quotation job is available for this order. A reviewed road distance and audit reason are required.',
      revision: false,
    };
  }
  return null;
}

export function assertManualQuoteReadback(
  orderId: string,
  quoteId: string,
  order: { id: string; current_quote_id: string | null; status: string } | null,
  quote: QuoteRow | null,
) {
  if (!order || order.id !== orderId || order.current_quote_id !== quoteId || !quote || quote.id !== quoteId || quote.order_id !== orderId || quote.status !== 'offered') {
    throw new ManualQuoteReadbackError(quoteId);
  }
  return { order, quote };
}

export function manualQuoteErrorMessage(error: unknown) {
  if (error instanceof ManualQuoteReadbackError) return error.message;
  const message = error instanceof Error ? error.message : String(error);
  if (/admin required|not authorized|jwt/i.test(message)) return 'Your admin session is no longer authorized. Sign in again, then refresh the order before taking any action.';
  if (/current status|cannot be priced|stale/i.test(message)) return 'The order changed while this form was open. Refresh it; do not resubmit against the stale state.';
  if (/pricing rule|supported distance|pricing boundaries/i.test(message)) return `The server rejected this quote under the current pricing rules: ${message}`;
  if (/item amounts|purchase budget|shopping orders|parcel orders/i.test(message)) return `Correct the purchase authorization: ${message}`;
  return `The quote was not confirmed: ${message}. If the connection failed after submission, refresh the order before trying again.`;
}
